import { Fragment, type ReactNode } from "react";
import {
  parseWelcomeText,
  type WelcomeInline,
} from "@shared-core/domain/welcome-text";

function inline(tokens: WelcomeInline[]): ReactNode[] {
  return tokens.map((token, index) => {
    switch (token.kind) {
      case "text":
        return <Fragment key={index}>{token.text}</Fragment>;
      case "line_break":
        return <br key={index} />;
      case "bold":
        return <strong key={index}>{inline(token.children)}</strong>;
      case "italic":
        return <em key={index}>{inline(token.children)}</em>;
      case "link":
        return (
          <a
            key={index}
            href={token.href}
            target="_blank"
            rel="noopener noreferrer"
            className="underline underline-offset-2"
          >
            {inline(token.children)}
          </a>
        );
    }
  });
}

/**
 * The welcome text, from the Markdown subset to React elements (security review M3): bold, italic, `https:` and
 * `mailto:` links, lists and paragraphs. Everything else is rendered as text; nothing is ever set as HTML.
 */
export function WelcomeText({
  text,
  className,
}: {
  text: string;
  className?: string;
}) {
  const blocks = parseWelcomeText(text);
  if (blocks.length === 0) return null;
  return (
    <div className={className}>
      {blocks.map((block, index) => {
        if (block.kind === "paragraph")
          return <p key={index}>{inline(block.children)}</p>;
        const Items = block.items.map((item, i) => (
          <li key={i}>{inline(item)}</li>
        ));
        return block.kind === "bulleted_list" ? (
          <ul key={index} className="list-disc space-y-0.5 pl-5">
            {Items}
          </ul>
        ) : (
          <ol key={index} className="list-decimal space-y-0.5 pl-5">
            {Items}
          </ol>
        );
      })}
    </div>
  );
}
